import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { User } from "@rago/web-shared";
import { api } from "../../../lib/apiClient";
import { auth } from "../../../lib/authManager";
import { useAuth } from "../../../lib/useAuth";
import {
  cachedPlayerPrefs,
  fetchPlayerPrefs,
  playerPrefsPatch,
  savePlayerPrefs,
  type BurnSubtitlesPolicy,
  type PlayerPrefs,
  type SubtitleColor,
  type SubtitlePosition,
  type SubtitleSize,
} from "../../../lib/playerPrefs";
import {
  LANGS,
  languagesFromUser,
  languagesPatch,
  type LanguagesForm,
} from "../../../lib/playbackLanguages";
import {
  BURN_POLICY_OPTIONS,
  SUBTITLE_COLOR_OPTIONS,
  SUBTITLE_POSITION_OPTIONS,
  SUBTITLE_SIZE_OPTIONS,
  subtitleCssVars,
} from "../../../lib/subtitleSettings";
import { Checkbox, errorMessage, Field, Select } from "../../../components/ui";
import {
  SheetHeader,
  SheetSection,
  UnsavedBar,
  useLastTouched,
} from "../../../components/settings-sheet";

export const Route = createFileRoute("/_authed/settings/player")({
  component: PlayerSettings,
});

/** One draft over two records: the playback prefs blob and the user's languages. */
type PlayerForm = PlayerPrefs & LanguagesForm;

function isClean(base: PlayerForm, form: PlayerForm): boolean {
  return (
    Object.keys(playerPrefsPatch(base, form)).length === 0 &&
    Object.keys(languagesPatch(base, form)).length === 0
  );
}

/**
 * Account-level player defaults (the Plex Web — Player page). Appearance
 * applies to subtitles the player draws itself (sidecar tracks); the burn
 * policy decides when the server converts them into the picture instead;
 * the playback languages pick the default audio/subtitle tracks. Edits sit
 * in one draft until the floating "Unsaved changes" pill's Save (settings-
 * sheet UnsavedBar, as on Settings › Trakt; Kenny), which writes the prefs
 * blob (/v1/me/settings) and the user record (/v1/user) as needed.
 */
function PlayerSettings() {
  const qc = useQueryClient();
  const state = useAuth();
  const user = state.status === "signedIn" ? state.user : null;
  const settings = useQuery({
    queryKey: ["me", "settings"],
    queryFn: fetchPlayerPrefs,
    // Offline / cloud unreachable: the localStorage mirror still applies.
    placeholderData: cachedPlayerPrefs(),
    retry: 1,
  });

  // baseline → draft → patch (the account page idiom): a null draft shows
  // the baseline; re-seed only while clean, so a background refetch never
  // clobbers edits in progress.
  const prefsBaseline = useMemo(() => settings.data ?? cachedPlayerPrefs(), [settings.data]);
  const baseline = useMemo<PlayerForm | null>(
    () => (user ? { ...prefsBaseline, ...languagesFromUser(user) } : null),
    [prefsBaseline, user],
  );
  const [draft, setDraft] = useState<PlayerForm | null>(null);
  const prevBaseline = useRef<PlayerForm | null>(null);
  useEffect(() => {
    if (!baseline) return;
    const prev = prevBaseline.current;
    setDraft((d) => (d && prev && isClean(prev, d) ? null : d));
    prevBaseline.current = baseline;
  }, [baseline]);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // The control changed last: it gets the focus back when the pill leaves.
  const touched = useLastTouched();

  if (!baseline) return null;
  const form = draft ?? baseline;
  const prefsPatch = playerPrefsPatch(baseline, form);
  const langPatch = languagesPatch(baseline, form);
  const dirty = Object.keys(prefsPatch).length > 0 || Object.keys(langPatch).length > 0;

  function update(p: Partial<PlayerForm>) {
    setDraft({ ...form, ...p });
    setSaved(false);
    setError(null);
  }

  function revert() {
    setDraft(null);
    setError(null);
  }

  async function save() {
    if (saving || !dirty) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    // This save's own answer: one that sends only the languages reached the
    // account, whatever an earlier one did.
    setOffline(false);
    // Prefs first. The mirror is written before the PATCH and the player
    // plays by it, so a cloud miss is a note, not an error; the account
    // copy catches up next time the cloud is reachable.
    if (Object.keys(prefsPatch).length > 0) {
      try {
        const merged = await savePlayerPrefs(prefsPatch);
        qc.setQueryData(["me", "settings"], merged);
      } catch {
        qc.setQueryData(["me", "settings"], { ...prefsBaseline, ...prefsPatch });
        setOffline(true);
      }
    }
    try {
      if (Object.keys(langPatch).length > 0) {
        auth.updateUser(await api<User>("/v1/user", { method: "PATCH", body: langPatch }));
      }
      setDraft(null);
      setSaved(true);
    } catch (err) {
      // The prefs half is already clean against the refreshed baseline, so
      // Save retries just the languages.
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const langOptions = LANGS.map(([value, label]) => (
    <option key={value} value={value}>
      {label}
    </option>
  ));

  return (
    // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
    <div className="rago-sheet-fill">
      <SheetHeader
        title="Player"
        lede="These defaults follow your account on every device. Color, size and position apply to subtitles the player draws; subtitles burned into the video keep their original look."
      />

      {/* Every control's change bubbles to here: the one changed last. */}
      <div onChange={touched.onChange}>
        <SheetSection title="Subtitles">
          <Field label="Subtitle color" htmlFor="subtitle-color">
            <Select
              id="subtitle-color"
              value={form.subtitleColor}
              onChange={(v) => update({ subtitleColor: v as SubtitleColor })}
            >
              {SUBTITLE_COLOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Subtitle position" htmlFor="subtitle-position">
            <Select
              id="subtitle-position"
              value={form.subtitlePosition}
              onChange={(v) => update({ subtitlePosition: v as SubtitlePosition })}
            >
              {SUBTITLE_POSITION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Subtitle size" htmlFor="subtitle-size">
            <Select
              id="subtitle-size"
              value={form.subtitleSize}
              onChange={(v) => update({ subtitleSize: v as SubtitleSize })}
            >
              {SUBTITLE_SIZE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>

          <div className="mb-4">
            <Checkbox
              id="subtitle-background"
              checked={form.subtitleBackground}
              onChange={(v) => update({ subtitleBackground: v })}
              label="Subtitle background"
              hint="Draw a dark box behind subtitle text. Off keeps a text shadow for readability."
            />
          </div>

          {/* Live preview of the appearance settings — the draft, so you see it before saving. */}
          <div className="relative mb-4 h-24 overflow-hidden rounded-[var(--rago-radius-m)] bg-black">
            <div
              className={`absolute inset-x-0 flex justify-center ${
                form.subtitlePosition === "top" ? "top-2" : "bottom-2"
              }`}
              style={subtitleCssVars(form)}
            >
              <span
                className="rago-sub-cue"
                style={{ fontSize: "calc(0.9rem * var(--rago-sub-scale, 1))" }}
              >
                How subtitles will look.
              </span>
            </div>
          </div>

          <Field
            label="Burn subtitles"
            htmlFor="burn-subtitles"
            hint="Determines when the server burns subtitles into the video during conversion. Automatic burns only image formats the player can't draw itself (PGS, VobSub). Always converts the video whenever subtitles are on. Never makes image-format subtitles unavailable."
          >
            <Select
              id="burn-subtitles"
              value={form.burnSubtitles}
              onChange={(v) => update({ burnSubtitles: v as BurnSubtitlesPolicy })}
            >
              {BURN_POLICY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        </SheetSection>

        <SheetSection title="Playback languages">
          <Field
            label="Preferred audio language"
            htmlFor="audio-language"
            hint="Players pick this track when available."
          >
            <Select
              id="audio-language"
              value={form.audioLang}
              onChange={(v) => update({ audioLang: v })}
            >
              {langOptions}
            </Select>
          </Field>
          <Field
            label="Preferred subtitle language"
            htmlFor="subtitle-language"
            hint="Also the default language when you search for subtitles."
          >
            <Select
              id="subtitle-language"
              value={form.subtitleLang}
              onChange={(v) => update({ subtitleLang: v })}
            >
              {langOptions}
            </Select>
          </Field>
        </SheetSection>
      </div>

      {/* The root's direct child (its dock rides at the sheet's foot), and
          always rendered, as UnsavedBar asks. */}
      <UnsavedBar
        dirty={dirty}
        saving={saving}
        saved={saved}
        savedLabel={offline ? "Saved on this device" : undefined}
        error={error ? errorMessage(error) : null}
        onSave={() => void save()}
        onRevert={revert}
        onFocusLost={touched.focus}
      />
    </div>
  );
}
